"use client";

import React from "react";
import styles from "./SoftwareTechStack.module.css";

const BRAND = "#F48428";

const CATEGORIES = [
  {
    title: "Programming Languages",
    items: [
      { label: "Java", icon: "fa-brands fa-java" },
      { label: "C#", icon: "fa-solid fa-code" },
      { label: "Python", icon: "fa-brands fa-python" },
      { label: "C++", icon: "fa-solid fa-code" },
      { label: "JavaScript", icon: "fa-brands fa-js" },
      { label: "PHP", icon: "fa-brands fa-php" },
    ],
  },
  {
    title: "Top Frameworks",
    items: [
      { label: "React", icon: "fa-brands fa-react" },
      { label: "Angular", icon: "fa-brands fa-angular" },
      { label: "Django", icon: "fa-solid fa-leaf" },
      { label: ".NET", icon: "fa-solid fa-circle-nodes" },
      { label: "Laravel", icon: "fa-brands fa-laravel" },
      { label: "Vue", icon: "fa-brands fa-vuejs" },
    ],
  },
];

import { useServiceData } from "../../hooks/useServiceData";

function getCategoryIcon(title) {
  if (/Language/i.test(title)) return "fa-solid fa-code";
  if (/Framework/i.test(title)) return "fa-solid fa-layer-group";
  return "fa-solid fa-circle";
}

function TechIcon({ icon, label }) {
  return (
    <div className="tech-item" title={label}>
      <div className="tech-icon">
        <i className={icon} aria-hidden></i>
      </div>
      <div className="tech-label">{label}</div>
      <style jsx>{`
        .tech-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          min-width: 100px;
        }
        .tech-icon {
          position: relative;
          width: 68px; height: 68px; border-radius: 16px;
          background: linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02));
          border: 1px solid rgba(255,255,255,0.14);
          display: grid; place-items: center;
          color: ${BRAND};
          box-shadow: 0 6px 18px rgba(0,0,0,0.25);
          transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .tech-icon::after { content: ""; position: absolute; inset: -2px; border-radius: 18px; background: radial-gradient(120% 120% at 50% -10%, ${BRAND}33, transparent 55%); pointer-events: none; transition: opacity .25s ease; opacity: .6; }
        .tech-icon :global(i) { font-size: 28px; }
        .tech-item:hover .tech-icon {
          transform: translateY(-2px);
          border-color: ${BRAND}55;
          box-shadow: 0 10px 22px rgba(0,0,0,0.35);
        }
        .tech-item:hover .tech-icon::after { opacity: 1; }
        .tech-label { font-size: 12px; color: rgba(255,255,255,0.85); }
      `}</style>
    </div>
  );
}

export default function SoftwareTechStack({ dynamicData }) {
  const { data: backendPageData, loading } = useServiceData();

  // If backend has a techStackSection in extraSections, skip rendering —
  // ExtraSectionsDispatcher will handle it to avoid duplicate sections.
  const hasTechStackSection = backendPageData?.extraSections?.some(s => s.type === 'techStackSection');
  if (!loading && hasTechStackSection && !dynamicData) return null;

  // Integrated dynamic data: Priority to prop -> Backend extraSections -> Static fallback
  const sourceData = dynamicData || backendPageData?.extraSections?.find(s => s.type === "techStackSection")?.refId;

  const sectionTag = sourceData?.tag || "Languages & Frameworks";
  const sectionTitle = sourceData?.title || "Advanced Technologies at Your Fingertips";
  const sectionSubtitle = sourceData?.subtitle || "We leverage a wide array of languages and frameworks to deliver exceptional software solutions.";

  // Comprehensive normalization to handle various backend structures
  const rawGroups = sourceData?.groups || sourceData?.categories || sourceData?.cards || sourceData?.items;

  const finalCategories = (Array.isArray(rawGroups) && rawGroups.length > 0)
    ? rawGroups.map((group, gIdx) => {
      const rawItems = group.items || group.cards || group.techs || group.data || [];
      return {
        title: group.groupTitle || group.title || group.name || `Category ${gIdx + 1}`,
        items: Array.isArray(rawItems) ? rawItems.map((it, iIdx) => ({
          label: it.name || it.label || it.title || `Tech ${iIdx + 1}`,
          icon: it.icon || "fa-solid fa-microchip"
        })) : []
      };
    })
    : CATEGORIES;
  return (
    <section
      className={`py-5 position-relative ${styles.root}`}
        style={{ background: "rgba(0,0,0,0)", "--brand-color": BRAND }}
    >
      <div className="container">
        <div className="text-center mb-4 head">
          <div className="eyebrow text-uppercase small">{sectionTag}</div>
          <h2 className="fw-bold title">{sectionTitle}</h2>
          <p className="subtitle m-0">{sectionSubtitle}</p>
        </div>

        <div className="stack-list">
          {finalCategories.map(({ title, items }) => (
            <div key={title} className="stack-card" data-reveal="fade">
              <div className="stack-title">
                <span className="rule" aria-hidden></span>
                <span className="label"><i className={getCategoryIcon(title)} aria-hidden></i> {title}</span>
              </div>
              <div className="icons">
                {items.map((it) => (
                  <TechIcon key={it.label} icon={it.icon} label={it.label} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
